import { getLogConsole, getLogService } from "@cervval.js/common/src/services/log.service";
import { anyObject, getProp, setProp } from "@cervval.js/common/src/tools/any.tools";
import { destroyObject } from "@cervval.js/common/src/tools/cache.tools";
import { toTimestamp } from "@cervval.js/common/src/tools/timestamps.tools.definitions";
import { ActionPersistSummary, DatedActionPersistSummary } from "../action.model";
import { ID, UID } from "../types";
import { Lazy } from "../utils/lazy";

/** Options for DTOCacheRepository constructor */
export type DTOCacheRepositoryOptions<DTO> = {
    /** ElementTypeName for the DTO, for debug purpose */
    elementTypeName?: string,
    isDummy?: boolean,// dummy => no data storage
    getId?: (item: DTO) => ID,
    setId?: (item: DTO, id: ID) => void,
    getUid?: (item: DTO) => UID,
    setUid?: (item: DTO, uid: UID) => void,
    dtoComparator?: (a: DTO, b: DTO) => number
}

/** Object stored in cacheItemMap */
export interface CacheItem<DTO> {
    dto: DTO;
}

/** Options for get and find methods of DTOCacheRepository */
export type DTOCacheAccessOptions = {
    /** 
     * use this.getUpToDateId, set this to true if you want to retrieve a
     * dto but only have a temporary id of it (negative id or deleted and recreated object id) 
     * * default is true
     */
    searchUpToDateId?: boolean,

    /** 
     * Do not log if DTO was not found 
     * * you should almost NEVER set quiet to true, if there is an error in the cache you wan't to know
     */
    quiet?: boolean
};

export class DTOCacheRepository<DTO, CacheInfo = unknown> {

    /**
     * If debugAccessWhenDataNotLoaded is true, a warning will be shown in the console if the application tries to access data before setData was called
     */
    public static debugAccessWhenDataNotLoaded = false;

    private _elementTypeName = "";

    /** Date of last setData or undefined if never called (date is in ms - it is not a timestamp !) */
    private _lastSetDataMs?: number = undefined;

    /** Date of last setData or undefined if never called (date is in ms - it is not a timestamp !) */
    public get lastSetData(): number | undefined {
        return this._lastSetDataMs;
    }

    /** Contains the data related to markDirty calls */
    private _dirtyData: DatedActionPersistSummary[] = [];

    /** Access dirty data array */
    public get dirtyData(): DatedActionPersistSummary[] {
        return this._dirtyData;
    }

    /** Date of last markDirty or undefined if never called (date is in ms - it is not a timestamp !) */
    public get lastMarkDirtyMs(): number | undefined {
        return this._dirtyData[this._dirtyData.length - 1]?.time;
    }

    /** When is dummy, the elements are not stored */
    private _isDummy = false;

    /** Map of cache item by ID  */
    private _itemsById: Map<ID, CacheItem<DTO>> = new Map();

    /** Map containing the deleted DTOs by ID */
    private _deletedItemsById: Map<ID, DTO> = new Map();

    /** Optional comparator to sort the dtoList */
    private _dtoComparator: ((a: DTO, b: DTO) => number) | undefined = undefined;

    private _cacheInfo: CacheInfo | undefined;

    /** Mapping between old IDs and new IDs */
    private _oldIdToNewId: { [oldId: number]: number } = {};

    // Lazy elements :

    /** List of all DTOs sorted with dtoComparator if not undefined (Lazy) */
    private _dtoList = new Lazy<DTO[]>(() => this._initDtoList());

    /** Map of DTOs by UID (Lazy -> Based on itemById) */
    private _itemsByUid = new Lazy<Map<UID, DTO>>(() => this._initItemsByUid());

    /** Map of deleted items by UID (Lazy -> Based on deletedItemsById) */
    private _deletedItemsByUid = new Lazy<Map<UID, DTO>>(() => this._initDeletedItemsByUid());

    /** once disposed this boolean is set to true */
    private _disposed: boolean = false;

    constructor(options: DTOCacheRepositoryOptions<DTO> = {}) {
        if (options.elementTypeName) {
            this._elementTypeName = options.elementTypeName;
        }
        if (options.isDummy) {
            this._isDummy = true;
        }
        if (options.getId) {
            this.getId = options.getId;
        }
        if (options.setId) {
            this.setId = options.setId;
        }
        if (options.getUid) {
            this.getUid = options.getUid;
        }
        if (options.setUid) {
            this._setUid = options.setUid;
        }
        if (options.dtoComparator) {
            this._dtoComparator = options.dtoComparator;
        }
    }

    /** 
     * cleanup memory for garbage collector to do its job 
     * once disposed, this repository is no more in a usable state (it is "destroyed")
     */
    public dispose(): void {
        if (this._disposed) { return; }
        this._itemsById.clear();
        this._deletedItemsById.clear();
        this.clearAllLazyElements();
        destroyObject(this);
        this._disposed = true;
    }

    /**
     * display a warning in the console if _lastSetDataMs is not set
     */
    public warnIfDataNotLoaded(): void {
        if (DTOCacheRepository.debugAccessWhenDataNotLoaded && this._lastSetDataMs == null) {
            getLogConsole().warn(this.elementTypeName + " was not loaded");
        }
    }

    /** @returns element TypeName */
    public get elementTypeName(): string {
        return this._elementTypeName;
    }

    /**
     * @returns initialized value of _list
     */
    public get dtoList(): DTO[] {
        this.warnIfDataNotLoaded();
        return this._dtoList.value;
    }

    /**
     * @returns initialized value of _itemsByUid
     */
    public get itemsByUid(): Map<UID, DTO> {
        this.warnIfDataNotLoaded();
        return this._itemsByUid.value;
    }

    /**
     * @returns initialized value of _deletedItemsByUid
     */
    public get deletedItemsByUid(): Map<UID, DTO> {
        return this._deletedItemsByUid.value;
    }

    /**
     * Returns a DTO by a given ID or throws error
     * @param id ID of the searched DTO
     * @param options optional
     * @param options.searchUpToDateId use this.getUpToDateId, set this to false if you are certain id is valid
     * @throws {Error} If DTO not found
     * @returns {DTO}
     */
    public getDTOById(id: ID, options?: DTOCacheAccessOptions): DTO {
        const dto = this.findDTOById(id, options);
        if (!dto) {
            throw new Error(`Cannot find CacheItem${this._elementTypeName ? " for " + this._elementTypeName : ""} with id : ${options?.searchUpToDateId ?? true ? `(${id})=>${this.getUpToDateId(id)}` : id}`);
        }
        return dto;
    }

    /**
     * Returns a DTO by a given ID and return undefined if not found
     * @param id ID of the searched DTO
     * @param options Options for get and find methods of DTOCacheRepository
     * @param options.searchUpToDateId use this.getUpToDateId, set this to false if you are certain id is valid
     * @param options.quiet if true does not show a warning in the console (you should almost NEVER set quiet to true, if there is an error in the cache you wan't to know)
     * @returns {DTO | undefined}
     */
    public findDTOById(id: ID, options?: DTOCacheAccessOptions): DTO | undefined {
        this.warnIfDataNotLoaded();
        const searchUpToDateId = options?.searchUpToDateId ?? true;
        const dto = this._itemsById.get(searchUpToDateId ? this.getUpToDateId(id) : id)?.dto;
        if (!dto && !options?.quiet) {
            getLogService().warn(`Cannot find CacheItem${this._elementTypeName ? " for " + this._elementTypeName : ""} with id : ${searchUpToDateId ? `(${id})=>${this.getUpToDateId(id)}` : id}`);
        }
        return dto;
    }

    /** @returns true if repository contains the element associated to id */
    public hasId(id: ID): boolean {
        this.warnIfDataNotLoaded();
        return this._itemsById.has(id);
    }

    /**
     * Replace all cache with a new list of DTOs
     * @param dataList List containing all the new DTOs
     * @param cacheInfo The  cache info corresponding to loaded data
     * @param setDataMs the date (is ms) when the data was loaded
     */
    public setData(dataList: DTO[], cacheInfo?: CacheInfo, setDataMs?: number): void {
        if (this._isDummy) { return; }
        const setDataTime = setDataMs ?? Date.now();
        this._lastSetDataMs = setDataTime;
        // we ony keep dirty entries that are after setDataTime
        this._dirtyData = this._dirtyData.filter((d) => {
            return d.time >= setDataTime;
        });
        this._cacheInfo = cacheInfo;
        const mapContent: [ID, CacheItem<DTO>][] = dataList.map(dto => [this.getId(dto), { dto }]);
        this._itemsById = new Map<ID, CacheItem<DTO>>(mapContent);
        this.clearAllLazyElements();
    }

    /** get cache info corresponding to loaded data */
    public getCacheInfo(): CacheInfo | undefined {
        return this._cacheInfo;
    }

    /** mark Data as dirty */
    public markDirty(updates?: ActionPersistSummary): void {
        const time = Date.now();
        if (updates) {
            this._dirtyData.push({ time: toTimestamp(time), ...updates });
        } else {
            this._dirtyData.push({ time: toTimestamp(time), global: true });
        }
    }

    /** @returns true if data was marked dirty after last setData or setData was never called */
    public isDirty(): boolean {
        const lastMarkDirtyMs = this.lastMarkDirtyMs;
        return this._lastSetDataMs == null || (lastMarkDirtyMs != null && lastMarkDirtyMs >= this._lastSetDataMs);
    }

    /**
     * Change the ID of a DTO
     * @param oldId Old ID
     * @param newId New ID
     */
    public changeId(oldId: ID, newId: ID): void {
        if (newId && oldId !== newId) {
            this._oldIdToNewId[oldId] = newId;
            const item = this._itemsById.get(oldId);
            if (item) {
                this._itemsById.set(newId, item); // Index item with new id
                this._itemsById.delete(oldId); // Remove old id
                this.setId(item.dto, newId);
            }
        }
    }

    /**
     * Add a DTO to the cache
     * @param {DTO} dto
     */
    public push(dto: DTO): void {
        if (this._isDummy) { return; }
        if (!this.getId(dto)) {
            throw new Error("Cannot push a dto without id in DTOCacheRepository" + (this._elementTypeName ? " for " + this._elementTypeName : ""));
        }
        this._itemsById.set(this.getId(dto), { dto });

        // Update Lazy
        this.itemsByUid.set(this.getUid(dto), dto);
        this._dtoList.clear(); // TODO remove the clear and add to list if it is init
    }

    /**
     * Add a list of DTO to the cache
     * @param {DTO[]} dtoList
     */
    public pushAll(dtoList: DTO[]): void {
        if (this._isDummy) { return; }
        dtoList.forEach(dto => this.push(dto));
    }
    /**
     * Remove DTO from the cache
     * @param  {DTO} dto
     * @returns void
     */
    public remove(dto: DTO): void {
        if (this._isDummy) { return; }
        this._itemsById.delete(this.getId(dto));
        this._deletedItemsById.set(this.getId(dto), dto);

        // Update Lazy is borken, dtoList is cleared below anyway...
        // CollectionTools.removeFirstFromArray(this.dtoList, dto, (a, b) => this.getId(a) === this.getId(b));
        this.itemsByUid.delete(this.getUid(dto));
        this.deletedItemsByUid.set(this.getUid(dto), dto);
        this._dtoList.clear();
    }

    /**
     * Can DTO be deleted in action ?
     */
    public canBeDeleted(dto: DTO): boolean {
        const elem = this._itemsById.get(this.getUpToDateId(this.getId(dto)));
        if (elem == null) { return false; }
        // TODO handle deletion marker, pas critique car on peut annuler deux fois de suite
        return true;
    }

    /**
     * This function is called after persistence to record the ids changes
     */
    public changeForeignKeyId(params: {
        elementId: number,
        field: string,
        oldId: number,
        newId: number
    }): void {
        const dto = this.findDTOById(this.getUpToDateId(params.elementId), { quiet: true });
        if (dto) {
            const fieldValue = getProp(dto, params.field);
            if (Array.isArray(fieldValue)) {
                for (let i = 0; i < fieldValue.length; i++) {
                    if (fieldValue[i] === params.oldId) {
                        fieldValue[i] = params.newId;
                    }
                }
            } else {
                if (fieldValue === params.oldId) {
                    setProp(dto, params.field, params.newId);
                }
            }
        }
    }

    /**
     * This function allows you to get the persisted id from a temporary id.
     * It also provides an up to date id for elements that were deleted and then restored with a new id.
     * @returns the updated id if a change was registered on this id, the passed id otherwise
     */
    public getUpToDateId(id: number): number {
        const upToDateId = this._oldIdToNewId[id];
        if (upToDateId && upToDateId !== id) {
            return this.getUpToDateId(upToDateId);
        }
        return id;
    }

    /**
     * If the element is found in the cache, the element from the cache is returned.
     * Otherwise, the original element is returned after calling ensureIdIsUpToDate and ensureForeignKeysAreUpToDate
     */
    public getUpToDateElement(element: DTO): DTO {
        const updToDateId = this.getUpToDateId(this.getId(element));
        return this.findDTOById(updToDateId, { quiet: true }) ?? element;
    }

    /** TODO */
    public ensureForeignKeysAreUpToDate(element: DTO): void {
        throw new Error("NIY");
    }

    /** TODO */
    public getForeignKeyTypeName(element: DTO, field: keyof DTO): string | undefined {
        throw new Error("NIY");
    }

    /**
     * Default implementation to get ID from a DTO
     * @param  {DTO} item
     * @returns ID
     */
    public getId(item: DTO): ID {
        return getProp(item as anyObject, "id");
    }

    /**
     * Default implementation to set ID of a DTO
     * @param item 
     * @param id 
     */
    public setId(item: DTO, id: ID): void {
        setProp(item as anyObject, "id", id);
    }

    /**
     * Default implementation to get UID from a DTO
     * @param  {DTO} item
     * @returns UID
     */
    public getUid(item: DTO): UID {
        return getProp(item as anyObject, "uid") ?? `${this.getId(item)}`;
    }

    /**
     * Default implementation to set UID of a DTO
     * @param item 
     * @param uid 
     */
    private _setUid(item: DTO, uid: UID): void {
        setProp(item as anyObject, "uid", uid);
    }

    /**
     * Set all lazy elements back to undefined
     */
    private clearAllLazyElements(): void {
        this._dtoList.clear();
        this._itemsByUid.clear();
        this._deletedItemsByUid.clear();
    }

    /**
     * Init the lazy dtoList variable
     */
    private _initDtoList(): DTO[] {
        let dtoList = [...this._itemsById.values()].map(itemsById => itemsById.dto);
        if (this._dtoComparator) {
            dtoList = dtoList.sort(this._dtoComparator);
        }
        return dtoList;
    }

    /**
     * Init the itemsByUid variable
     */
    private _initItemsByUid(): Map<UID, DTO> {
        const mapContent: [UID, DTO][] = [...this._itemsById.values()]
            .map(itemsById => itemsById.dto)
            .map(dto => [this.getUid(dto), dto]);

        return new Map<UID, DTO>(mapContent);
    }

    /**
     * Init the deletedItemsByUid variable
     */
    private _initDeletedItemsByUid(): Map<UID, DTO> {
        const mapContent: [UID, DTO][] = [...this._deletedItemsById.values()]
            .map(item => [this.getUid(item), item]);

        return new Map<UID, DTO>(mapContent);
    }

}